/** @jsxImportSource @emotion/react */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { css } from '@emotion/react';

import {
  useFolderOrganizationQuery,
  useFolderAssetsQuery,
} from '../../../actions/FolderActions';
import { useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';

import { useUserQuery } from '../../../actions/UserActions';

import Skeleton from '@mui/material/Skeleton';

import UploadFileDropzone from '../../upload/UploadFileDropzone';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';

import Text from '@mui/material/Typography';
import TextField from '@mui/material/TextField';

import { useDebouncedValue } from '../../../utils/functions';
import {
  useRestHeight,
  useIsMobile,
  useAssetWidth,
} from '../../../utils/hooks';

import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FindInPageOutlinedIcon from '@mui/icons-material/FindInPageOutlined';

import SearchIcon from '@mui/icons-material/Search';

import IconButton from '@mui/material/IconButton';
import ViewListOutlinedIcon from '@mui/icons-material/ViewListOutlined';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import SortIcon from '@mui/icons-material/Sort';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ClearIcon from '@mui/icons-material/Clear';

import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Tooltip from '@mui/material/Tooltip';

import Checkbox from '@mui/material/Checkbox';

import Select from '../../common/Select';
import DateRange from '../../common/DateRange';

import Asset from './Asset';
import Folder from './Folder';

import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file

function noop() {}

function SortMenu({ sort, setSort }) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);

  const items = useMemo(() => {
    return [
      { label: t('Namn'), value: { key: 'name', order: 'asc' } },
      { label: t('Skapad'), value: { key: 'created_at', order: 'asc' } },
      { label: t('Uppdaterad'), value: { key: 'updated_at', order: 'asc' } },
    ];
  }, [t]);

  return (
    <>
      <Tooltip title={t('Sortera')}>
        <IconButton
          css={css`
            margin-left: auto;
          `}
          onClick={(e) => {
            setAnchorEl(e.currentTarget);
          }}
        >
          <SortIcon />
        </IconButton>
      </Tooltip>
      <Menu
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
      >
        {items.map((x) => (
          <MenuItem
            key={x.label}
            css={css`
              color: ${sort.key === x.value.key ? 'var(--primary-main)' : ''};
            `}
            onClick={() => {
              setSort((prev) => {
                if (prev.key === x.value.key) {
                  return {
                    key: x.value.key,
                    order: prev.order === 'asc' ? 'desc' : 'asc',
                  };
                }
                return {
                  key: x.value.key,
                  order: x.value.order,
                };
              });
            }}
          >
            <ListItemText>{x.label}</ListItemText>
            {sort.key === x.value.key ? (
              <ListItemIcon
                css={css`
                  margin-left: 0.3rem;
                `}
              >
                {sort.order === 'asc' ? (
                  <ArrowUpwardIcon color="primary" fontSize="small" />
                ) : (
                  <ArrowDownwardIcon color="primary" fontSize="small" />
                )}
              </ListItemIcon>
            ) : null}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

function AssetArea({
  assets,
  onDone,
  folderPrefix,
  compact,
  folders,
  uploadingFiles,
  setUploadingFiles,
  dropzone = true,
  isModal = false,
  hideOptions = false,
  singleSelect = false,
  onLocationChange,
  selectedAssetsObj,
  onSelectionChange,
  onSelectAll,
  allSelected,
  deletePermission = false,
  onShowExport,
  isLoadingFolders,
  isLoadingAssets,
  onAssetSelect,
  onFolderSelect,
  folderCounter,
  flexBasis = 239,
  userFallbackFolder = false,
  types,
}) {
  const { t } = useTranslation();
  const { ref, setRef, maxHeight } = useRestHeight([isLoadingFolders], 16);
  const { isMobile } = useIsMobile();
  const assetWidth = useAssetWidth(ref, flexBasis);

  let folderComponents;

  let isLoading = isLoadingFolders || isLoadingAssets;

  if (isLoading) {
    if (compact) {
      folderComponents = Array.from(new Array(3)).map((_, idx) => (
        <tr key={idx}>
          <td colSpan={10}>
            <Skeleton variant="text" width="100%" height={54} key={idx} />
          </td>
        </tr>
      ));
    } else {
      folderComponents = Array.from(new Array(3)).map((_, idx) => (
        <Skeleton variant="rectangle" width={250} height={80} key={idx} />
      ));
    }
  } else {
    folderComponents = folders?.map((x) => (
      <Folder
        key={`folder_${x.id}`}
        singleSelect={singleSelect}
        disabled={!types.includes('folder')}
        compact={compact}
        isModal={isModal}
        hideOptions={hideOptions}
        onLocationChange={onLocationChange}
        deletePermission={deletePermission}
        selectedAssets={selectedAssetsObj}
        folderPrefix={x.folder_prefix}
        onSelectionChange={(folder) => {
          if (onFolderSelect) {
            onFolderSelect(folder);
          }
        }}
        folderCounter={folderCounter}
        width={assetWidth}
        {...x}
      />
    ));
  }

  let assetComponents;
  if (isLoading) {
    if (compact) {
      assetComponents = Array.from(new Array(3)).map((_, idx) => (
        <tr key={idx}>
          <td colSpan={10}>
            <Skeleton variant="text" width="100%" height={54} key={idx} />
          </td>
        </tr>
      ));
    } else {
      assetComponents = Array.from(new Array(3)).map((_, idx) => (
        <Skeleton variant="rectangle" width={250} height={80} key={idx} />
      ));
    }
  } else {
    assetComponents = (assets || []).map((x) => (
      <Asset
        key={`asset_${x.id}`}
        singleSelect={singleSelect}
        compact={compact}
        isModal={isModal}
        disabled={(isModal && x.type === 'document') || !types.includes(x.type)}
        {...x}
        selected={!!selectedAssetsObj?.[x.id]}
        onSelectionChange={(data) => {
          if (onAssetSelect) {
            onAssetSelect(data);
          }
        }}
        deletePermission={deletePermission}
        onShowExport={onShowExport}
        hideOptions={hideOptions}
        width={assetWidth}
        userFallbackFolder={userFallbackFolder}
      />
    ));
  }

  let uploadingAssets = uploadingFiles.map((x) => (
    <Asset
      key={`uploading_${x.name}`}
      singleSelect={singleSelect}
      compact={compact}
      uploading
      progress={x.progress}
      file
      {...x}
      isModal={isModal}
      hideOptions={hideOptions}
      width={assetWidth}
    />
  ));

  const columns = [t('Namn'), t('Status'), t('Senast ändrad')];

  let zone = null;

  const empty =
    !folders?.length && !uploadingAssets?.length && !assetComponents?.length;

  if (compact) {
    zone = (
      <Table>
        <TableHead>
          <TableRow
            css={css`
              font-weight: 600;
            `}
          >
            {columns.map((x, i) => (
              <TableCell key={`column_${x}`}>
                <Text fontWeight="600" variant="subtitle2">
                  {i === 0 && isModal && !singleSelect ? (
                    <Checkbox
                      css={css`
                        padding: 0px;
                      `}
                      checked={allSelected.checked}
                      indeterminate={allSelected.indeterminate}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        e.stopPropagation();
                        onSelectAll();
                      }}
                    />
                  ) : null}
                  {x}
                </Text>
              </TableCell>
            ))}
            {!isModal ? (
              <TableCell
                css={css`
                  text-align: right;
                `}
              >
                <MoreVertIcon />
              </TableCell>
            ) : null}
          </TableRow>
        </TableHead>
        <TableBody>
          {folderComponents}
          {uploadingAssets}
          {assetComponents}
        </TableBody>
      </Table>
    );
  } else {
    zone = (
      <div
        css={css`
          display: flex;
          gap: 8px;
          flex-direction: column;
        `}
      >
        {folders?.length > 0 ? (
          <>
            <Text variant="subtitle2" fontWeight="600">
              {t('Mappar')}
            </Text>
            <div
              css={css`
                display: flex;
                gap: 8px;
                flex-wrap: wrap;
              `}
            >
              {folderComponents}
            </div>
          </>
        ) : null}
        {uploadingAssets?.length > 0 || assetComponents?.length > 0 ? (
          <Text variant="subtitle2" fontWeight="600">
            {t('Filer')}
          </Text>
        ) : null}
        <div
          css={css`
            display: flex;
            gap: 8px;
            flex-wrap: wrap;
            min-height: ${empty ? '400px' : '0'};
          `}
        >
          {uploadingAssets}
          {assetComponents}
          {empty && !isMobile && dropzone ? (
            <div
              css={css`
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 8px;
                width: 100%;
              `}
            >
              <DescriptionOutlinedIcon />
              <Text>{t('Släpp filer här')}</Text>
            </div>
          ) : null}
          {empty && !isMobile && !dropzone ? (
            <div
              css={css`
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 8px;
                width: 100%;
              `}
            >
              <FindInPageOutlinedIcon />
              <Text>{t('Den här mappen är tom')}</Text>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  if (dropzone) {
    zone = (
      <UploadFileDropzone
        onUploadProgress={(f) => {
          setUploadingFiles(Object.values(f));
        }}
        onUploadDone={onDone}
        folderPrefix={folderPrefix}
        noClick
      >
        {zone}
      </UploadFileDropzone>
    );
  }
  return (
    <div
      ref={setRef}
      css={css`
        overflow-y: auto;
        ${!isMobile ? `max-height: ${maxHeight}px;` : ''}
      `}
    >
      {zone}
    </div>
  );
}

export default function AssetTree({
  folderPrefix,
  onLocationChange = noop,
  hideFilters = false,
  singleSelect,
  fallbackFolder,
  isModal = false,
  hideOptions = false,
  selectedAssets = {},
  onSelect = noop,
  onShowExport = noop,
  onFolderSelect = noop,
  onAssetSelect = noop,
  folderCounter = {},
  flexBasis = 239,
  types = ['folder', 'file', 'document', 'transcript', 'meetings'],
}) {
  const { user } = useUserQuery();
  const { isMobile } = useIsMobile();
  const workspace = user?.workspace;
  const folderId = folderPrefix
    .split('/')
    .filter((x) => x)
    .pop();
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [compact, setCompact] = useState(
    localStorage.getItem('compact') === 'true'
  );
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [selectionRange, setSelectionRange] = useState({
    startDate: null,
    endDate: null,
    key: 'selection',
  });
  const [type, setType] = useState('');
  let _sort = localStorage.getItem('assets-sort');
  const [sort, setSort] = useState(
    _sort ? JSON.parse(_sort) : { key: 'name', order: 'asc' }
  );
  const [searchText, setSearchText] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);

  const debouncedText = useDebouncedValue(searchText, 300);

  const { assets: _assets, isLoading: isLoadingAssets } = useFolderAssetsQuery(
    folderId,
    {
      type: type ? type : null,
      types: types.length === 1 && types[0] === 'folder' ? [] : types, // We want to see files when selecting folder
      startDate: selectionRange.startDate,
      query: debouncedText,
      includeTranscriptSnapshot: 1,
      endDate: selectionRange.endDate,
      enabled:
        folderPrefix &&
        folderPrefix.split('/').length > 2 &&
        ((!selectionRange.startDate && !selectionRange.endDate) ||
          (!!selectionRange.startDate &&
            !!selectionRange.endDate &&
            selectionRange.startDate < selectionRange.endDate)),
    }
  );

  const selectableTypes = useMemo(() => {
    const rv = [
      {
        value: 'all',
        label: t('Alla filer'),
      },
    ];

    if (types.includes('transcript')) {
      rv.push({
        value: 'transcription',
        label: t('Transkribering'),
      });
    }

    if (types.includes('meetings')) {
      rv.push({
        value: 'meetings',
        label: t('Möten'),
      });
    }

    if (types.includes('file')) {
      rv.push({
        value: 'files',
        label: t('Dokument'),
      });
    }

    return rv;
  }, [types, t]);

  const assets = useMemo(() => {
    if (!sort || !_assets?.length) return _assets;
    if (sort.key === 'name') {
      return _assets.sort((a, b) => {
        if (sort.order === 'asc') {
          return a.name.localeCompare(b.name);
        }
        return b.name.localeCompare(a.name);
      });
    }
    if (sort.key === 'created_at') {
      return _assets.sort((a, b) => {
        if (sort.order === 'asc') {
          return new Date(a.created_at) - new Date(b.created_at);
        }
        return new Date(b.created_at) - new Date(a.created_at);
      });
    }
    return _assets.sort((a, b) => {
      if (sort.order === 'asc') {
        return new Date(a.updated_at) - new Date(b.updated_at);
      }
      return new Date(b.updated_at) - new Date(a.updated_at);
    });
  }, [sort, _assets]);

  const {
    folders: _folders,
    currentFolder,
    isLoading: isLoadingFolders,
  } = useFolderOrganizationQuery({
    organizationId: workspace?.id,
    types,
    folderPrefix,
    type: type ? type : null,
    startDate: selectionRange.startDate,
    query: debouncedText,
    endDate: selectionRange.endDate,
  });

  const folders = useMemo(() => {
    let x = _folders.filter((x) => !(x.user_fallback && x.file_count === 0));
    if (!sort || !x?.length) return x;
    if (sort.key === 'name') {
      return x.sort((a, b) => {
        if (sort.order === 'asc') {
          return a.name.localeCompare(b.name);
        }
        return b.name.localeCompare(a.name);
      });
    }
    if (sort.key === 'created_at') {
      return x.sort((a, b) => {
        if (sort.order === 'asc') {
          return new Date(a.created_at) - new Date(b.created_at);
        }
        return new Date(b.created_at) - new Date(a.created_at);
      });
    }
    return x.sort((a, b) => {
      if (sort.order === 'asc') {
        return new Date(a.updated_at) - new Date(b.updated_at);
      }
      return new Date(b.updated_at) - new Date(a.updated_at);
    });
  }, [sort, _folders]);

  const onDone = () => {
    setUploadingFiles([]);
    queryClient.invalidateQueries(['folderAssets', folderId]);
  };

  const flipCompact = useCallback(() => {
    setCompact((c) => {
      localStorage.setItem('compact', !c);
      return !c;
    });
  }, []);

  useEffect(() => {
    localStorage.setItem('assets-sort', JSON.stringify(sort));
  }, [sort]);

  const allAssetsSelected = useMemo(() => {
    const curr = folderCounter[folderPrefix];
    return {
      checked: curr?.total && curr?.total === curr?.selected,
      indeterminate: curr?.selected > 0 && curr?.selected < curr?.total,
    };
  }, [folderCounter, folderPrefix]);

  const deletePermission = useMemo(() => {
    return (
      currentFolder?.user_role === 'admin' ||
      currentFolder?.user_role === 'edit'
    );
  }, [currentFolder?.user_role]);

  useEffect(() => {
    // Block reload on upload
    function beforeUnload(e) {
      if (uploadingFiles.length) {
        e.preventDefault();
        e.returnValue = '';
      }
    }
    window.addEventListener('beforeunload', beforeUnload);
    return () => {
      window.removeEventListener('beforeunload', beforeUnload);
    };
  }, [uploadingFiles]);

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 14px;

        min-height: min(500px, 100vh/2);
      `}
    >
      <div
        css={css`
          display: flex;
          align-items: center;
        `}
      >
        {!isMobile ? (
          <>
            <div
              css={css`
                display: flex;
                gap: 16px;
                align-items: center;
                flex-grow: 1;
              `}
            >
              {!hideFilters && (
                <>
                  <Select
                    options={selectableTypes}
                    label={t('Typ')}
                    size="small"
                    css={css`
                      width: 100%;
                      max-width: 8rem;
                    `}
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                  />
                  <DateRange
                    selectionRange={selectionRange}
                    onRangeChange={setSelectionRange}
                  />
                </>
              )}
            </div>
            <div
              css={css`
                margin-left: auto;
              `}
            >
              {searchOpen ? (
                <TextField
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder={t('Sök')}
                  size="small"
                  autoFocus
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        onClick={() => setSearchText('')}
                        disabled={!searchText}
                      >
                        <ClearIcon />
                      </IconButton>
                    ),
                  }}
                />
              ) : (
                <IconButton onClick={() => setSearchOpen(true)}>
                  <SearchIcon />
                </IconButton>
              )}
            </div>
            <Tooltip
              title={compact ? t('Visa som rutnät') : t('Visa som lista')}
            >
              <IconButton onClick={() => flipCompact()}>
                {compact ? <GridViewOutlinedIcon /> : <ViewListOutlinedIcon />}
              </IconButton>
            </Tooltip>
            <SortMenu sort={sort} setSort={setSort} />
          </>
        ) : null}
      </div>
      <AssetArea
        assets={assets}
        singleSelect={singleSelect}
        onDone={onDone}
        folderPrefix={folderPrefix}
        compact={!isMobile && compact}
        folders={folders}
        isLoadingAssets={isLoadingAssets}
        isLoadingFolders={isLoadingFolders}
        uploadingFiles={uploadingFiles}
        setUploadingFiles={setUploadingFiles}
        dropzone={!isModal && !currentFolder?.user_fallback}
        isModal={isModal}
        hideOptions={hideOptions}
        onLocationChange={onLocationChange}
        selectedAssetsObj={selectedAssets}
        allSelected={allAssetsSelected}
        onFolderSelect={onFolderSelect}
        onAssetSelect={onAssetSelect}
        onSelectAll={() => {
          onFolderSelect(folderPrefix);
        }}
        deletePermission={deletePermission}
        onShowExport={onShowExport}
        folderCounter={folderCounter}
        flexBasis={flexBasis}
        userFallbackFolder={currentFolder?.user_fallback}
        types={types}
      />
    </div>
  );
}
